/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import { withNamespaces } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { CheckboxButton } from '../../../../ui/Form/CheckboxButton';
import { Container, Column, Row } from '../../../../../styles/Grid';
import { SafePureComponent } from '../../../../../utils/SafePureComponent';
import { withConsumer } from '../../../../../contexts';
import { SolutionBuilderConsumer } from '../../../../../contexts/SolutionBuilderContext';
import NumberPicker from '../../NumberPicker';
import { SidePanel } from '../../SidePanel';
import { SOLUTION_BUILDER as dict } from '../../../../../utils/dictionarykeys';
import * as commonStyles from '../../index.styles';
import * as styles from './index.styles';

const defaultData = {
  NumbersHeadline: { value: '' },
  NumbersSubtitle: { value: '' },
  NumbersSidePanelCTA: { value: '' },
  NumbersOfficeText: { value: '' },
  NumbersDedicatedOfficeText: { value: '' },
  NumbersOfficeMembershipText: { value: '' },
  NumbersCoworkingText: { value: '' },
  NumbersDedicatedDeskText: { value: '' },
  NumbersCoworkMembershipText: { value: '' },
  NumbersMaxPeopleNumber: { value: '' },
  NumbersFootNote: { value: '' },
  NumbersSidePanelHeadline: { value: '' },
  NumbersSidePanelDedicatedOffice: { value: '' },
  NumbersSidePanelOfficeMembership: { value: '' },
  NumbersSidePanelDedicatedDesk: { value: '' },
  NumbersSidePanelCoworkMembership: { value: '' },
};

export class SelectWorkspaceOptions extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);
  numberPickerPlaceholder = this.props.t(dict.PEOPLE);

  state = {
    isSidePanelOpen: false,
  };

  componentDidMount() {
    const { manager } = this.props;
    const initialValues = manager.getStepData();

    manager.setHelpText(get(this.fields, 'NumbersFootNote.value', ''));
    this.toggleNextButton(initialValues);
  }

  openSidePanel = () => {
    this.setState({ isSidePanelOpen: true });
  };

  closeSidePanel = () => {
    this.setState({ isSidePanelOpen: false });
  };

  toggleNextButton = formValues => {
    const { manager } = this.props;
    const isNextEnabled =
      formValues.officeNumber1 ||
      formValues.officeNumber2 ||
      formValues.coworkingNumber1 ||
      formValues.coworkingNumber2;

    if (isNextEnabled) manager.enableNext();
    else manager.disableNext();
  };

  onFormSubmit = values => {
    const { manager } = this.props;
    manager.setStepData(values);
  };

  renderHeadline() {
    const {
      NumbersHeadline,
      NumbersSubtitle,
      NumbersSidePanelCTA,
    } = this.fields;

    return (
      <Column cols={{ sm: 6 }}>
        <h1 css={commonStyles.headline}>{get(NumbersHeadline, 'value', '')}</h1>
        <p css={commonStyles.description}>
          {get(NumbersSubtitle, 'value', '')}
        </p>
        <div css={commonStyles.secondaryCTAContainer}>
          <button
            onClick={this.openSidePanel}
            css={commonStyles.secondaryCTAButton}
          >
            {get(NumbersSidePanelCTA, 'value', '')}
          </button>
        </div>
      </Column>
    );
  }

  showHelpMessage = () => {
    this.props.manager.showHelpMessage();
  };

  renderSidePanel() {
    return (
      <SidePanel
        isSidePanelOpen={this.state.isSidePanelOpen}
        header={this.renderSidePanelHeading}
        body={this.renderSidePanelBody}
        onRequestClose={this.closeSidePanel}
        toggleSidePanel={this.closeSidePanel}
      />
    );
  }

  renderSidePanelHeading = () => {
    return this.fields.NumbersSidePanelHeadline.value;
  };

  renderSidePanelBody = () => {
    const {
      NumbersSidePanelDedicatedOffice,
      NumbersSidePanelOfficeMembership,
      NumbersSidePanelDedicatedDesk,
      NumbersSidePanelCoworkMembership,
    } = this.fields;

    return (
      <React.Fragment>
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={NumbersSidePanelDedicatedOffice}
        />
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={NumbersSidePanelOfficeMembership}
        />
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={NumbersSidePanelDedicatedDesk}
        />
        <RichText
          css={[
            commonStyles.sidePanel.section,
            commonStyles.sidePanel.richText,
          ]}
          field={NumbersSidePanelCoworkMembership}
        />
      </React.Fragment>
    );
  };

  /**
   * Needs to be attached to `validate` Formik method as a hack since
   * there's no callback for a common onChange.
   * @see https://github.com/formium/formik/issues/1633
   */
  onFormInputChange = formValues => {
    this.toggleNextButton(formValues);
  };

  renderForm() {
    const { manager } = this.props;
    const initialValues = manager.getStepData();
    const {
      NumbersOfficeText,
      NumbersDedicatedOfficeText,
      NumbersOfficeMembershipText,
      NumbersCoworkingText,
      NumbersDedicatedDeskText,
      NumbersCoworkMembershipText,
      NumbersMaxPeopleNumber,
    } = this.fields;
    const max = Number(get(NumbersMaxPeopleNumber, 'value', ''));

    return (
      <Formik
        onSubmit={this.onFormSubmit}
        initialValues={initialValues}
        validate={this.onFormInputChange}
        render={({
          handleSubmit,
          values,
          handleChange,
          submitForm,
          setFieldValue,
        }) => {
          manager.setSubmitFormTrigger(submitForm);

          return (
            <form css={styles.form} onSubmit={handleSubmit}>
              <div css={styles.checkboxContainer}>
                <div css={styles.checkboxWrapper}>
                  <CheckboxButton
                    innerCss={{
                      container: styles.checkbox,
                      labelChecked: styles.labelChecked,
                    }}
                    id="office"
                    onChange={e => {
                      handleChange(e);
                      this.showHelpMessage();
                      if (!e.target.checked) {
                        setFieldValue('officeNumber1', '');
                        setFieldValue('officeNumber2', '');
                      }
                    }}
                    checked={values.office || false}
                  >
                    <RichText field={NumbersOfficeText} />
                  </CheckboxButton>
                  {values.office && (
                    <div css={styles.numbersContainer}>
                      <NumberPicker
                        id="officeNumber1"
                        innerCss={{
                          container: styles.firstNumberPicker,
                          label: styles.numberPickerLabel,
                          controls: styles.numberPickerControls,
                        }}
                        label={get(NumbersDedicatedOfficeText, 'value')}
                        numberLabel={this.numberPickerPlaceholder}
                        value={values.officeNumber1}
                        max={max}
                        onChange={value =>
                          setFieldValue('officeNumber1', value)
                        }
                      />
                      <NumberPicker
                        id="officeNumber2"
                        innerCss={{
                          container: styles.numberPickerContainer,
                          label: styles.numberPickerLabel,
                          controls: styles.numberPickerControls,
                        }}
                        label={get(NumbersOfficeMembershipText, 'value')}
                        numberLabel={this.numberPickerPlaceholder}
                        value={values.officeNumber2}
                        max={max}
                        onChange={value =>
                          setFieldValue('officeNumber2', value)
                        }
                      />
                    </div>
                  )}
                </div>
                <div css={styles.checkboxWrapper}>
                  <CheckboxButton
                    innerCss={{
                      container: styles.checkbox,
                      labelChecked: styles.labelChecked,
                    }}
                    id="coworking"
                    onChange={e => {
                      handleChange(e);
                      this.showHelpMessage();
                      if (!e.target.checked) {
                        setFieldValue('coworkingNumber1', '');
                        setFieldValue('coworkingNumber2', '');
                      }
                    }}
                    checked={values.coworking || false}
                  >
                    <RichText field={NumbersCoworkingText} />
                  </CheckboxButton>
                  {values.coworking && (
                    <div css={styles.numbersContainer}>
                      <NumberPicker
                        id="coworkingNumber1"
                        innerCss={{
                          container: styles.firstNumberPicker,
                          label: styles.numberPickerLabel,
                          controls: styles.numberPickerControls,
                        }}
                        label={get(NumbersDedicatedDeskText, 'value')}
                        numberLabel={this.numberPickerPlaceholder}
                        value={values.coworkingNumber1}
                        max={max}
                        onChange={value =>
                          setFieldValue('coworkingNumber1', value)
                        }
                      />
                      <NumberPicker
                        id="coworkingNumber2"
                        innerCss={{
                          container: styles.numberPickerContainer,
                          label: styles.numberPickerLabel,
                          controls: styles.numberPickerControls,
                        }}
                        label={get(NumbersCoworkMembershipText, 'value')}
                        numberLabel={this.numberPickerPlaceholder}
                        value={values.coworkingNumber2}
                        max={max}
                        onChange={value =>
                          setFieldValue('coworkingNumber2', value)
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>
          );
        }}
      />
    );
  }

  safeRender() {
    return (
      <div css={commonStyles.container}>
        <Container>
          <Row>{this.renderHeadline()}</Row>
          <Row>{this.renderForm()}</Row>
          {this.renderSidePanel()}
        </Container>
      </div>
    );
  }
}

SelectWorkspaceOptions.displayName = 'SelectWorkspaceOptions';

SelectWorkspaceOptions.defaultProps = {
  t: key => key,
};

SelectWorkspaceOptions.propTypes = {
  t: PropTypes.func,
  manager: PropTypes.any,
  fields: PropTypes.any,
};

export default compose(
  withNamespaces(),
  withConsumer(SolutionBuilderConsumer, 'manager'),
)(SelectWorkspaceOptions);
